import React, { useCallback, useEffect, useRef, useState } from 'react';
import { authAPI } from '../services/api';
import { getLang } from './LanguageToggle';
import './IdleTimeout.css';

const IDLE_LIMIT_MS = 60 * 60 * 1000;
const COUNTDOWN_SECONDS = 30;
const CHECK_INTERVAL_MS = 5 * 1000;

const TRANSLATIONS = {
  en: {
    title: 'Are you still there?',
    body: 'You have been inactive for a while. For your security, you will be signed out automatically.',
    countdown: 'Signing out in',
    seconds: 'seconds',
    stay: "I'm here",
    logout: 'Log out now',
  },
  fr: {
    title: 'Êtes-vous toujours là?',
    body: 'Vous êtes inactif depuis un certain temps. Pour votre sécurité, vous serez déconnecté automatiquement.',
    countdown: 'Déconnexion dans',
    seconds: 'secondes',
    stay: 'Je suis là',
    logout: 'Se déconnecter maintenant',
  },
};

const IdleTimeout = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const lastActivityRef = useRef(Date.now());
  const showPromptRef = useRef(false);
  const loggingOutRef = useRef(false);
  const countdownTimerRef = useRef(null);
  const checkTimerRef = useRef(null);

  const lang = getLang();
  const t = (key) => (TRANSLATIONS[lang] || TRANSLATIONS.en)[key] || key;

  const performLogout = useCallback(async () => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    try { await authAPI.logout(); } catch (_) {}
    window.location.replace('/login');
  }, []);

  const recordActivity = useCallback(() => {
    if (showPromptRef.current) return;
    lastActivityRef.current = Date.now();
  }, []);

  const dismissPrompt = useCallback(() => {
    showPromptRef.current = false;
    setShowPrompt(false);
    setSecondsLeft(COUNTDOWN_SECONDS);
    lastActivityRef.current = Date.now();
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((ev) => window.addEventListener(ev, recordActivity, { passive: true }));

    // When the user returns to the PRISM tab, reset the idle counter AND verify the
    // session is still alive. If the cookie expired while they were away, /me returns
    // 401 and the global interceptor (services/api.js) navigates to /login. This is
    // how we avoid "looks logged in but next click jumps to login".
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        recordActivity();
        authAPI.getCurrentUser().catch(() => {});
      }
    };
    const onFocus = () => recordActivity();
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', onFocus);

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, recordActivity));
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', onFocus);
    };
  }, [recordActivity]);

  useEffect(() => {
    checkTimerRef.current = setInterval(() => {
      // Only count idle time while the user is actually looking at PRISM.
      if (document.hidden) return;
      if (showPromptRef.current || loggingOutRef.current) return;
      if (Date.now() - lastActivityRef.current >= IDLE_LIMIT_MS) {
        showPromptRef.current = true;
        setShowPrompt(true);
        setSecondsLeft(COUNTDOWN_SECONDS);
      }
    }, CHECK_INTERVAL_MS);
    return () => {
      if (checkTimerRef.current) clearInterval(checkTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!showPrompt) return undefined;
    // Accumulate countdown progress only while the tab is visible. If the user
    // switches away mid-countdown the popup waits for them to come back — we never
    // auto-logout in the background.
    let elapsedMs = 0;
    let visibleSince = !document.hidden ? Date.now() : null;
    setSecondsLeft(COUNTDOWN_SECONDS);

    const onVis = () => {
      if (document.hidden) {
        if (visibleSince !== null) {
          elapsedMs += Date.now() - visibleSince;
          visibleSince = null;
        }
      } else {
        visibleSince = Date.now();
      }
    };
    document.addEventListener('visibilitychange', onVis);

    countdownTimerRef.current = setInterval(() => {
      if (document.hidden) return;
      const liveMs = visibleSince !== null ? Date.now() - visibleSince : 0;
      const elapsed = Math.floor((elapsedMs + liveMs) / 1000);
      const remaining = COUNTDOWN_SECONDS - elapsed;
      if (remaining <= 0) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
        performLogout();
        return;
      }
      setSecondsLeft(remaining);
    }, 250);

    return () => {
      document.removeEventListener('visibilitychange', onVis);
      if (countdownTimerRef.current) {
        clearInterval(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
    };
  }, [showPrompt, performLogout]);

  if (!showPrompt) return null;

  const pct = Math.max(0, Math.min(100, (secondsLeft / COUNTDOWN_SECONDS) * 100));

  return (
    <div
      className="idle-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="idle-timeout-title"
    >
      <div className="idle-modal">
        <h2 id="idle-timeout-title" className="idle-modal-title">{t('title')}</h2>
        <p className="idle-modal-body">{t('body')}</p>

        <div className="idle-countdown" aria-live="polite" aria-atomic="true">
          <span className="idle-countdown-label">{t('countdown')}</span>
          <span className="idle-countdown-value">{secondsLeft}</span>
          <span className="idle-countdown-unit">{t('seconds')}</span>
        </div>

        <div className="idle-countdown-bar" aria-hidden="true">
          <div className="idle-countdown-bar-fill" style={{ width: `${pct}%` }} />
        </div>

        <div className="idle-modal-actions">
          <button
            type="button"
            className="idle-modal-btn idle-modal-btn-primary"
            onClick={dismissPrompt}
            autoFocus
          >
            {t('stay')}
          </button>
          <button
            type="button"
            className="idle-modal-btn idle-modal-btn-secondary"
            onClick={performLogout}
          >
            {t('logout')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default IdleTimeout;
